import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { Utils } from 'src/app/services/utils';
import {
  ayaAppUrlForTier,
  resolveAyaDeployTierFromReturnUrl,
  wwwAppUrlForTier,
} from 'src/app/utils/aya-deploy-urls';
import {
  resolveFinalLogoutLandingUrl,
} from 'src/app/utils/cross-app-logout';

@Component({
  selector: 'app-logout',
  templateUrl: './logout.component.html',
  styleUrls: ['./logout.component.scss'],
})
export class LogoutComponent implements OnInit, OnDestroy {
  private readonly errorRedirectSeconds = 4;
  private secondsCountdown = this.errorRedirectSeconds;
  private countdownSubscription = new Subscription();
  private logoutReturnHint: string | null = null;

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _authService: AuthService
  ) {}

  async ngOnInit() {
    const snapshot = this._route.snapshot.queryParams;
    const rawReturnUrl = snapshot['returnUrl'] || null;
    this.logoutReturnHint = rawReturnUrl ? String(rawReturnUrl).trim() : null;
    const everywhereRaw = String(snapshot['everywhere'] || '').toLowerCase();
    const everywhere =
      everywhereRaw === '' ||
      everywhereRaw === '1' ||
      everywhereRaw === 'true' ||
      everywhereRaw === 'yes';

    // Session from Lovable lives on another origin — accept token via query once.
    this._authService.hydrateAuthFromLogoutDeepLink({
      token: snapshot['token'],
      refreshToken: snapshot['refreshToken'],
      email: snapshot['email'],
    });

    this.stripSensitiveQueryParams();

    if (rawReturnUrl) {
      this._authService.captureDeepLinkReturnUrl(rawReturnUrl);
    }

    const trustedReturnUrl = this._authService.deepLinkReturnUrl;

    try {
      await this._authService.logoutAndRedirect({
        returnUrl: trustedReturnUrl,
        everywhere,
      });
    } catch (err: unknown) {
      console.warn('[logout] Sign-out did not complete fully', err);
      this.redirectToWwwLanding();
    }
  }

  /** Prefer aya-health-chat (dev-www / qa-www) when server sign-out fails. */
  private redirectToWwwLanding(): void {
    const chainedLanding = resolveFinalLogoutLandingUrl(this.logoutReturnHint);
    const sanitizedChainLanding = chainedLanding
      ? Utils.sanitizeReturnUrl(chainedLanding)
      : null;
    if (sanitizedChainLanding) {
      window.location.replace(sanitizedChainLanding);
      return;
    }
    const tier =
      resolveAyaDeployTierFromReturnUrl(this.logoutReturnHint) ?? 'dev';
    const www = Utils.sanitizeReturnUrl(
      tier === 'qa'
        ? wwwAppUrlForTier('qa')
        : environment.ayaHealthChatAppUrl
    );
    if (www) {
      try {
        const landing = new URL(www);
        landing.searchParams.set('loggedOut', '1');
        window.location.replace(landing.href);
      } catch {
        window.location.replace(www);
      }
      return;
    }
    this.redirectToLoginAfterDelay();
  }

  private redirectToLoginAfterDelay(): void {
    this.secondsCountdown = this.errorRedirectSeconds;
    this.startTimer();
  }

  private stripSensitiveQueryParams(): void {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      url.searchParams.delete('refreshToken');
      url.searchParams.delete('email');
      window.history.replaceState({}, '', url.toString());
    } catch {
      // ignore
    }
  }

  private startTimer() {
    this.countdownSubscription.unsubscribe();
    this.countdownSubscription = timer(0, 1000).subscribe(() => {
      --this.secondsCountdown;
      if (this.secondsCountdown <= 0) {
        this.countdownSubscription.unsubscribe();
        const chainedLanding = resolveFinalLogoutLandingUrl(this.logoutReturnHint);
        const sanitizedChainLanding = chainedLanding
          ? Utils.sanitizeReturnUrl(chainedLanding)
          : null;
        if (sanitizedChainLanding) {
          window.location.replace(sanitizedChainLanding);
          return;
        }
        const tier =
          resolveAyaDeployTierFromReturnUrl(this.logoutReturnHint) ?? 'dev';
        const www = Utils.sanitizeReturnUrl(
          tier === 'qa'
            ? wwwAppUrlForTier('qa')
            : environment.ayaHealthChatAppUrl
        );
        if (www) {
          try {
            const landing = new URL(www);
            landing.searchParams.set('loggedOut', '1');
            window.location.replace(landing.href);
          } catch {
            window.location.replace(www);
          }
          return;
        }
        const ayaFallback = Utils.sanitizeReturnUrl(
          tier === 'qa' ? ayaAppUrlForTier('qa') : environment.ayaAppUrl
        );
        if (ayaFallback) {
          try {
            const landing = new URL(ayaFallback);
            landing.searchParams.set('loggedOut', '1');
            window.location.replace(landing.href);
          } catch {
            window.location.replace(ayaFallback);
          }
          return;
        }
        window.location.replace('/login?loggedOut=1');
      }
    });
  }

  ngOnDestroy() {
    this.countdownSubscription.unsubscribe();
  }
}
